import { useState, useEffect, useRef, useCallback } from "react";
// useEffect and useRef used by MenuBar, useState used by SubMenu
import { getTigerResource } from "../utils/tigerResource";
import { UI_THEMES, useUiTheme, type UiThemeId } from "../theme/uiTheme";
import { countToolWindows, subscribeToolWindows, getOpenToolWindowCount } from "../utils/closeToolWindows";
import { getLiveModeEnabled, getViewRunState, setViewMode, getReplayServerSession } from "../utils/viewMode";
import { onViewModeChange } from "../utils/useFollowViewMode";
import * as replay from "../utils/replayControls";
import { getEnableTurbineSerial } from "../utils/turbineSerial";

export interface MenuAction {
  action: string;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface MenuItemDef {
  label: string;
  action?: string;
  disabled?: boolean;
  separator?: boolean;
  children?: MenuItemDef[];
}

function sep(): MenuItemDef {
  return { label: "", separator: true };
}

// Labels resolve through `get_tiger_resource` (mirroring the C++ TigerMainX
// menu construction), so the menus pick up site/language overrides. The menus
// are built per-render rather than at module load so overrides loaded after
// import are reflected.
export function buildFileMenu(
  importCsdEnabled: boolean,
  liveModeEnabled: boolean,
  replayPlaying: boolean,
  isLive: boolean,
  replayServerSession: boolean,
  changePasswordEnabled: boolean = false,
  signOutEnabled: boolean = false,
  exitEnabled: boolean = true,
  /** Active UI chrome theme; when given, File > Theme lists the choices with
   *  a dot on this one. Omitted (tests, callers without a theme) → no entry. */
  uiThemeId?: UiThemeId | null,
): MenuItemDef[] {
  const themeChildren: MenuItemDef[] = UI_THEMES.map((th) => ({
    label: uiThemeId === th.id ? `● ${th.label}` : th.label,
    action: `file.uitheme.${th.id}`,
  }));

  return [
    ...(liveModeEnabled
      // Greyed only while a replay is actively playing: loading a new window
      // would interrupt it. Re-enabled once the replay stops / finishes / is
      // paused (the user has stopped to look around) or in live mode.
      ? [{ label: getTigerResource("TIGERUI_LOAD_DATA"), action: "file.loadData", disabled: replayPlaying }, sep()]
      : []),
    {
      label: getTigerResource("TIGERUI_EXPORT"),
      children: [
        { label: getTigerResource("TIGERUI_EXPORT_DATA"), action: "file.export.data" },
        { label: getTigerResource("TIGERUI_EXPORT_TREND"), action: "file.export.trend" },
      ],
    },
    // C++ add_file_menu: Backup shows when data_source == ONLINE (Live);
    // Restore shows in offline/replay. Both are local-data operations, so they
    // only appear on non-broker hosts (replay_server_session disabled).
    ...(liveModeEnabled && isLive && !replayServerSession
      ? [{ label: getTigerResource("TIGERUI_BACKUP"), action: "file.backupData" }]
      : []),
    ...(liveModeEnabled && !isLive && !replayServerSession
      ? [{ label: getTigerResource("TIGERUI_RESTORE"), action: "file.restoreData" }]
      : []),
    // Sites tracking turbine serials get Import CSD (C++ add_file_menu;
    // the label is hardcoded there too).
    ...(importCsdEnabled ? [{ label: getTigerResource("TIGERUI_IMPORT_CSD"), action: "file.importCsd" }] : []),
    // UI chrome theme (dialogs/menus) — see src/theme/uiTheme.ts.
    ...(uiThemeId != null ? [{ label: getTigerResource("TIGERUI_THEMES"), children: themeChildren }] : []),
    sep(),
    { label: getTigerResource("TIGERUI_PRINT"), action: "file.print" },
    { label: getTigerResource("TIGERUI_COPY_VIEW"), action: "file.copyView" },
    { label: getTigerResource("TIGERUI_SAVE_VIEW"), action: "file.saveView" },
    // Web deployment only, and only when a local password exists to change
    // (db_auth/mfa_auth). Under ad_auth the directory owns the password.
    ...(changePasswordEnabled || signOutEnabled ? [sep()] : []),
    ...(changePasswordEnabled
      ? [{ label: getTigerResource("TIGERUI_CHANGE_PASSWORD"), action: "file.changePassword" }]
      : []),
    // Web deployment with authentication enabled: end the session and return to
    // the login screen. Meaningless on the desktop (and under `none`), where
    // there is no session to drop.
    ...(signOutEnabled
      ? [{ label: getTigerResource("TIGERUI_SIGN_OUT"), action: "file.signOut" }]
      : []),
    // Desktop only. In a browser tab there is no application to exit: the item
    // led to a confirmation dialog whose OK could only call `window.close()`,
    // which browsers ignore for pages the script didn't open. Web users end a
    // session with Sign Out (when authentication is on) or by closing the tab.
    ...(exitEnabled
      ? [sep(), { label: getTigerResource("TIGERUI_EXIT"), action: "file.exit" }]
      : []),
  ];
}

export interface ReportMenuItem {
  name: string;
  label: string;
}

// Per-turbine payload of the `get_report_menu` command (see the Rust
// commands::get_report_menu): config-driven entries plus enable flags for the
// built-in reports.
export interface ReportMenuTurbine {
  config_name: string;
  turbine_name: string;
  additional_reports: ReportMenuItem[];
  additional_events: ReportMenuItem[];
  stability: boolean;
}

// One turbine's report items, in the C++ build_tools_report_cascade order:
// custom reports, custom events (each followed by a separator when present),
// the conditionally-enabled built-ins, then the always-present Controller
// Values entry. (The C++ also lists Report Compare; deliberately not ported.)
function turbineReportItems(t: ReportMenuTurbine): MenuItemDef[] {
  const items: MenuItemDef[] = [];

  if (t.additional_reports.length > 0) {
    for (const r of t.additional_reports) {
      items.push({
        label: getTigerResource(r.label),
        action: `tools.report.custom.${t.config_name}:${r.name}`,
      });
    }
    items.push(sep());
  }

  if (t.additional_events.length > 0) {
    for (const e of t.additional_events) {
      items.push({
        label: getTigerResource(e.label),
        action: `tools.report.event.${t.config_name}:${e.name}`,
      });
    }
    items.push(sep());
  }

  // Only the wired built-in reports are listed. NOX / NOX Average / Equivalent
  // Hours / Air Filter / Gas Composition / Maintenance were never wired and are
  // not required, so they're intentionally omitted (removed 2026-06-30).
  const builtins: [boolean, string, string][] = [
    [t.stability, "TIGERUI_REPORT_STABILITY", "stability"],
  ];
  for (const [enabled, resource, kind] of builtins) {
    if (enabled) {
      items.push({
        label: getTigerResource(resource),
        action: `tools.report.${kind}.${t.config_name}`,
      });
    }
  }

  items.push({
    label: getTigerResource("TIGERUI_REPORT_VALUES"),
    action: `tools.report.controller.${t.config_name}`,
  });
  return items;
}

// Tools > Reports submenu. With a single turbine its items sit directly under
// Reports; with several, each turbine gets its own cascade (C++
// build_tools_report_cascade). Null until the backend data arrives.
export function buildReportsMenu(turbines: ReportMenuTurbine[]): MenuItemDef | null {
  if (turbines.length === 0) return null;
  const children =
    turbines.length === 1
      ? turbineReportItems(turbines[0])
      : turbines.map((t) => ({ label: t.turbine_name, children: turbineReportItems(t) }));
  return { label: getTigerResource("TIGERUI_REPORTS"), children };
}

// Tools > Start Tiger cascade: one entry per turbine, firing
// `tools.startTiger.<config_name>` (App routes it to utils/startTiger, the same
// path as an ALARM_START_TIGER view button). Only the alarm-server interface
// starts turbine displays — a -live UI (desktop `-live` / web `?live=`) already
// IS one turbine's display — so the cascade is hidden there, and until the
// turbine list (shared with the Reports cascade) arrives. Always a cascade, even
// for one turbine, so the turbine name stays visible on the item.
export function buildStartTigerMenu(
  turbines: ReportMenuTurbine[],
  liveModeEnabled: boolean,
): MenuItemDef | null {
  if (liveModeEnabled || turbines.length === 0) return null;
  return {
    label: getTigerResource("TIGERUI_START_TIGER"),
    children: turbines.map((t) => ({
      label: t.turbine_name,
      action: `tools.startTiger.${t.config_name}`,
    })),
  };
}

function buildToolsMenu(reportTurbines: ReportMenuTurbine[], liveModeEnabled: boolean): MenuItemDef[] {
  const items: MenuItemDef[] = [
    { label: getTigerResource("TIGERUI_DATAGRID_RES"), action: "tools.dataGrid" },
    {
      label: getTigerResource("TIGERUI_GRAPH"),
      children: [
        { label: getTigerResource("TIGERUI_GRAPH_DATA"), action: "tools.graph.data" },
        { label: getTigerResource("TIGERUI_GRAPH_XY"), action: "tools.graph.xy" },
        { label: getTigerResource("TIGERUI_GRAPH_MULTI"), action: "tools.graph.multiTime" },
        { label: getTigerResource("TIGERUI_GRAPH_EVENT"), action: "tools.graph.event" },
      ],
    },
    {
      label: getTigerResource("TIGERUI_TREND"),
      children: [
        { label: getTigerResource("TIGERUI_TREND_GRAPH"), action: "tools.trend.graph" },
        { label: getTigerResource("TIGERUI_TREND_XYGRAPH"), action: "tools.trend.xy" },
        { label: getTigerResource("TIGERUI_TREND_MULTI_XYGRAPH"), action: "tools.trend.multiXY" },
      ],
    },
  ];
  const reports = buildReportsMenu(reportTurbines);
  if (reports) items.push(reports);
  const startTiger = buildStartTigerMenu(reportTurbines, liveModeEnabled);
  if (startTiger) items.push(sep(), startTiger);
  return items;
}

function buildDiagnosticsMenu(diagFilterEnabled: boolean, liveModeEnabled: boolean): MenuItemDef[] {
  return [
    { label: getTigerResource("TIGERUI_DIAG"), action: "diagnostics.view" },
    {
      label: getTigerResource("TIGERUI_DIAG_FILTERS"),
      action: "diagnostics.filters",
      // C++ greys this out unless a turbine has a diag_latching.ini filter file
      // (get_diag_filter_enabled).
      disabled: !diagFilterEnabled,
    },
    // Live DV_ALARM messages window (bottom panel) controls — only on the -live
    // interface; the alarm interface has no live message feed.
    ...(liveModeEnabled
      ? [
          { label: getTigerResource("TIGERUI_DIAG_TOGGLE_MESSAGES"), action: "diagnostics.toggleMessages" },
          { label: getTigerResource("TIGERUI_DIAG_CLEAR_MESSAGES"), action: "diagnostics.clearMessages" },
        ]
      : []),
  ];
}

function buildWindowMenu(toolWindowsOpen: boolean): MenuItemDef[] {
  return [
    {
      label: getTigerResource("TIGERUI_SHOW_ALL_TOOL_WIN"),
      action: "window.showAll",
      // C++ greys this unless at least one tool window is open, with the same
      // flag as Close All (set_window_menu_cascade_states). On the webserver the
      // tool windows are window.open popups (separate OS windows the browser does
      // let a script raise), so show-all works there too — same enable rule.
      disabled: !toolWindowsOpen,
    },
    { label: getTigerResource("TIGERUI_TOOL_SETS"), action: "window.toolSets" },
    {
      label: getTigerResource("TIGERUI_CLOSE_ALL_TOOL_WIN"),
      action: "window.closeAll",
      // C++ greys this unless at least one tool window is open
      // (set_window_menu_cascade_states / Ui_get_num_tool_windows).
      disabled: !toolWindowsOpen,
    },
    {
      label: getTigerResource("TIGERUI_TOOL_WINS_SAVE"),
      action: "window.save",
      // C++ greys this with the same flag as Close All / Show All — there's
      // nothing to save into a report when no tool windows are open
      // (set_window_menu_cascade_states → tool_wins_save_button).
      disabled: !toolWindowsOpen,
    },
  ];
}

// `turbineManualPresent` mirrors C++ Ti_help_turbine_manual_enable: the Turbine
// Manuals entry only appears when the turbine's configured manual file exists.
function buildHelpMenu(turbineManualPresent: boolean): MenuItemDef[] {
  return [
    { label: getTigerResource("TIGERUI_MANUALS_TIGER"), action: "help.tigerManual" },
    ...(turbineManualPresent
      ? [{ label: getTigerResource("TIGERUI_MANUALS_TURBINE"), action: "help.turbineManuals" }]
      : []),
    sep(),
    { label: getTigerResource("TIGERUI_ABOUT"), action: "help.about" },
  ];
}

// Advanced menu: manual data-file replay, shown only on the -live UI when this
// host is NOT a replay_server_session host (the non-broker path picks files
// directly). C++ application_shell advanced cascade → OnApp_advanced_file_replay_button.
// Greyed only while a replay is actively playing: loading a new data file would
// interrupt it (mirrors File > Load Data). Re-enabled once the replay stops /
// finishes / is paused.
function buildAdvancedMenu(replayPlaying: boolean): MenuItemDef[] {
  return [
    { label: getTigerResource("TIGERUI_SELECT_DATA_FILE"), action: "advanced.selectDataFile", disabled: replayPlaying },
  ];
}

function buildMenuBar(
  diagFilterEnabled: boolean,
  toolWindowsOpen: boolean,
  reportTurbines: ReportMenuTurbine[],
  importCsdEnabled: boolean,
  turbineManualPresent: boolean,
  liveModeEnabled: boolean,
  advancedMenuEnabled: boolean,
  replayPlaying: boolean,
  isLive: boolean,
  replayServerSession: boolean,
  changePasswordEnabled: boolean,
  signOutEnabled: boolean = false,
  uiThemeId: UiThemeId | null = null,
): { label: string; items: MenuItemDef[] }[] {
  return [
    {
      label: getTigerResource("TIGERUI_FILE"),
      items: buildFileMenu(importCsdEnabled, liveModeEnabled, replayPlaying, isLive, replayServerSession, changePasswordEnabled, signOutEnabled, isTauri(), uiThemeId),
    },
    { label: getTigerResource("TIGERUI_TOOLS"), items: buildToolsMenu(reportTurbines, liveModeEnabled) },
    { label: getTigerResource("TIGERUI_DIAG"), items: buildDiagnosticsMenu(diagFilterEnabled, liveModeEnabled) },
    ...(advancedMenuEnabled ? [{ label: getTigerResource("TIGERUI_ADVANCED"), items: buildAdvancedMenu(replayPlaying) }] : []),
    { label: getTigerResource("TIGERUI_WINDOW"), items: buildWindowMenu(toolWindowsOpen) },
    { label: getTigerResource("TIGERUI_HELP"), items: buildHelpMenu(turbineManualPresent) },
  ];
}

function ReplayToolbar({ onMenuAction }: { onMenuAction: (action: string) => void }) {
  const [active, setActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const s = await replay.getReplayStatus();
      if (!cancelled && s) {
        setActive(s.active);
        setPaused(s.paused);
        setLoaded(s.loaded_end > s.loaded_start);
        setRate(s.rate_level);
      }
    };
    poll();
    const id = setInterval(poll, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const run = (fn: () => Promise<unknown> | unknown) => () => { Promise.resolve(fn()).catch((e) => console.error("replay control failed:", e)); };
  // Start is available only when data is loaded but the replay isn't running
  // (after load, after Stop, or once it finishes) — C++ enable_start. When the
  // replay isn't running every other control is disabled, leaving only Start.
  const canStart = loaded && !active;

  // Media icons as inline SVGs. The previous Unicode glyphs (⏪ ▶ ⏸ ⏹ …) came
  // from the system emoji font, whose per-glyph metrics sit differently inside
  // the line box — they can't be vertically centred reliably, and several
  // rendered as colour emoji. fill=currentColor keeps the .rt-play / .rt-stop /
  // hover / disabled colours working.
  const rtIcon = {
    width: 12,
    height: 12,
    viewBox: "0 0 16 16",
    fill: "currentColor",
    "aria-hidden": true,
    style: { display: "block" },
  } as const;
  const iconPlay = <svg {...rtIcon}><polygon points="4,2.5 13.5,8 4,13.5" /></svg>;
  const iconPause = <svg {...rtIcon}><rect x="4" y="3" width="3" height="10" rx="0.5" /><rect x="9" y="3" width="3" height="10" rx="0.5" /></svg>;

  return (
    <div className="replay-toolbar" role="toolbar" aria-label={getTigerResource("TIGER_UI_REPLAY_CONTROLS")}>
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_LOAD_DATA")} aria-label={getTigerResource("TIGER_UI_RT_LOAD_DATA")} disabled={active && !paused} onClick={() => onMenuAction("file.loadData")}>
        {/* History (clock + counter-clockwise arrow): "replay a past time". */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block" }}>
          <path d="M3 3v5h5" />
          <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
          <polyline points="12 7 12 12 15 15" />
        </svg>
      </button>
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_JUMP_BACK_60")} aria-label={getTigerResource("TIGER_UI_RT_JUMP_BACK")} disabled={!active} onClick={run(() => replay.replayJump(false))}>
        <svg {...rtIcon}><polygon points="8,3 2,8 8,13" /><polygon points="14,3 8,8 14,13" /></svg>
      </button>
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_JUMP_FORWARD_60")} aria-label={getTigerResource("TIGER_UI_RT_JUMP_FORWARD")} disabled={!active} onClick={run(() => replay.replayJump(true))}>
        <svg {...rtIcon}><polygon points="2,3 8,8 2,13" /><polygon points="8,3 14,8 8,13" /></svg>
      </button>
      <span className="rt-sep" />
      <button className="rt-btn rt-play" title={getTigerResource("TIGER_UI_RT_START_REPLAY")} aria-label={getTigerResource("TIGER_UI_RT_START")} disabled={!canStart} onClick={run(replay.replayStart)}>{iconPlay}</button>
      <button className="rt-btn" title={paused ? getTigerResource("TIGER_UI_RT_CONTINUE") : getTigerResource("TIGER_UI_RT_PAUSE")} aria-label={paused ? getTigerResource("TIGER_UI_RT_CONTINUE") : getTigerResource("TIGER_UI_RT_PAUSE")} disabled={!active}
        onClick={run(() => (paused ? replay.replayPlay() : replay.replayPause()))}>{paused ? iconPlay : iconPause}</button>
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_STEP_ONE")} aria-label={getTigerResource("TIGER_UI_RT_STEP")} disabled={!active || !paused} onClick={run(replay.replayStep)}>
        <svg {...rtIcon}><polygon points="3,3 10.5,8 3,13" /><rect x="12" y="3" width="2.5" height="10" rx="0.5" /></svg>
      </button>
      <button className="rt-btn rt-stop" title={getTigerResource("TIGER_UI_RT_STOP_REPLAY")} aria-label={getTigerResource("TIGER_UI_RT_STOP")} disabled={!active} onClick={run(replay.replayStop)}>
        <svg {...rtIcon}><rect x="3.5" y="3.5" width="9" height="9" rx="1" /></svg>
      </button>
      <span className="rt-sep" />
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_SLOWER")} aria-label={getTigerResource("TIGER_UI_RT_RATE_DOWN")} disabled={!active} onClick={run(() => replay.replaySetRate(Math.max(0.25, rate / 2)))}>−</button>
      <span className="rt-rate">{rate}×</span>
      <button className="rt-btn" title={getTigerResource("TIGER_UI_RT_FASTER")} aria-label={getTigerResource("TIGER_UI_RT_RATE_UP")} disabled={!active} onClick={run(() => replay.replaySetRate(Math.min(64, rate * 2)))}>+</button>
    </div>
  );
}

interface SubMenuProps {
  items: MenuItemDef[];
  onAction: (action: string) => void;
  onClose: () => void;
  depth?: number;
  // For flyouts (depth > 0): a fixed viewport position computed from the parent
  // item, so the submenu escapes the parent's clipped scroll area instead of
  // being cut off. Top-level menus (depth 0) are positioned by CSS.
  position?: { left: number; top: number };
  // Max height for this menu's scroll area so it never runs off the bottom of
  // the screen (the items scroll inside it via arrows/wheel instead).
  maxHeight?: number;
}

// Pixels scrolled per auto-scroll tick while an arrow is hovered.
const MENU_SCROLL_STEP = 28;
// Gap kept between a menu and the screen edges.
const MENU_VIEWPORT_MARGIN = 8;

function SubMenu({ items, onAction, onClose, depth = 0, position, maxHeight }: SubMenuProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const holdTimer = useRef<number | null>(null);
  const [arrows, setArrows] = useState<{ up: boolean; down: boolean }>({ up: false, down: false });
  const [flyout, setFlyout] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const up = el.scrollTop > 0;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setArrows((a) => (a.up === up && a.down === down ? a : { up, down }));
  }, []);

  // Recompute the arrows whenever the items (and so the content height) change.
  useEffect(updateArrows, [items, maxHeight, updateArrows]);

  // Position the hovered item's flyout as a fixed overlay anchored to the item,
  // capped to the space left below it.
  useEffect(() => {
    const child = hoveredIndex !== null ? items[hoveredIndex]?.children : undefined;
    const itemEl = hoveredIndex !== null ? itemRefs.current[hoveredIndex] : null;
    if (!child || child.length === 0 || !itemEl) {
      setFlyout(null);
      return;
    }
    const r = itemEl.getBoundingClientRect();
    setFlyout({
      left: r.right,
      // Align the flyout's first item with the parent item (it sits inside the
      // dropdown padding), and cap its scroll area to the space below, leaving
      // room for the dropdown padding + scroll arrows so it never runs off-screen.
      top: r.top - MENU_VIEWPORT_MARGIN,
      maxHeight: window.innerHeight - r.top - 48,
    });
  }, [hoveredIndex, items]);

  const stopHold = useCallback(() => {
    if (holdTimer.current !== null) {
      clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  const startHold = useCallback(
    (dir: number) => {
      stopHold();
      const step = () => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop += dir * MENU_SCROLL_STEP;
        updateArrows();
      };
      step();
      holdTimer.current = window.setInterval(step, 60);
    },
    [stopHold, updateArrows],
  );

  useEffect(() => stopHold, [stopHold]);

  const dropdownStyle = position
    ? { position: "fixed" as const, left: position.left, top: position.top }
    : undefined;

  return (
    <div
      className="menu-dropdown"
      style={dropdownStyle}
      onMouseLeave={() => setHoveredIndex(null)}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {arrows.up && (
        <div
          className="menu-scroll-arrow up"
          onMouseEnter={() => startHold(-1)}
          onMouseLeave={stopHold}
        >
          &#9650;
        </div>
      )}
      <div
        className="menu-scroll-area"
        ref={scrollRef}
        style={maxHeight !== undefined ? { maxHeight } : undefined}
        onScroll={updateArrows}
        onWheel={(e) => {
          const el = scrollRef.current;
          if (!el) return;
          el.scrollTop += e.deltaY;
          updateArrows();
        }}
      >
        {items.map((item, i) => {
          if (item.separator) {
            // Keep itemRefs index-aligned with `items`.
            return (
              <div key={i} className="menu-separator" ref={(el) => { itemRefs.current[i] = el; }} />
            );
          }

          const hasChildren = item.children && item.children.length > 0;

          return (
            <div
              key={i}
              ref={(el) => { itemRefs.current[i] = el; }}
              className={`menu-item${item.disabled ? " disabled" : ""}${hoveredIndex === i ? " hover" : ""}`}
              onMouseEnter={() => setHoveredIndex(i)}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled) return;
                if (item.action) {
                  onAction(item.action);
                  onClose();
                }
              }}
            >
              <span className="menu-item-label">{item.label}</span>
              {hasChildren && <span className="menu-arrow">&#9656;</span>}
            </div>
          );
        })}
      </div>
      {arrows.down && (
        <div
          className="menu-scroll-arrow down"
          onMouseEnter={() => startHold(1)}
          onMouseLeave={stopHold}
        >
          &#9660;
        </div>
      )}
      {flyout && hoveredIndex !== null && items[hoveredIndex]?.children && (
        <SubMenu
          items={items[hoveredIndex].children!}
          onAction={onAction}
          onClose={onClose}
          depth={depth + 1}
          position={{ left: flyout.left, top: flyout.top }}
          maxHeight={flyout.maxHeight}
        />
      )}
    </div>
  );
}

interface MenuBarProps {
  onMenuAction: (action: string) => void;
  /** True on the web build when the signed-in user has a password to change
   *  (db_auth / mfa_auth). Adds File > Change Password. */
  changePasswordEnabled?: boolean;
  /** True on the web build when authentication is enabled, so there is a session
   *  to end. Adds File > Sign Out. */
  signOutEnabled?: boolean;
}

export function MenuBar({
  onMenuAction,
  changePasswordEnabled = false,
  signOutEnabled = false,
}: MenuBarProps) {
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  // Defaults enabled; greyed only after the backend reports no filter file
  // exists (matches C++ get_diag_filter_enabled). Stays enabled in browser/tests.
  const [diagFilterEnabled, setDiagFilterEnabled] = useState(true);
  // Whether any graph / data-grid / diagnostics tool window is open. Drives the
  // Window > "Close All Tool Windows" enabled state (C++ Ui_get_num_tool_windows).
  // Recounted each time a menu opens; defaults disabled (none open at startup).
  const [toolWindowsOpen, setToolWindowsOpen] = useState(false);
  // Per-turbine report entries for the Tools > Reports cascade. Empty until
  // the backend responds; the cascade is hidden while empty.
  const [reportTurbines, setReportTurbines] = useState<ReportMenuTurbine[]>([]);
  // File > Import CSD only exists on sites tracking turbine serials
  // (enable_turbine_serial); hidden until the backend confirms.
  const [importCsdEnabled, setImportCsdEnabled] = useState(false);
  // Help > Turbine Manuals only appears when a turbine's configured manual file
  // exists (C++ Ti_help_turbine_manual_enable). Hidden until the backend
  // confirms, so it stays hidden in browser/tests where there's no manual.
  const [turbineManualPresent, setTurbineManualPresent] = useState(false);
  // -live mode: show a Live/Replay switch. Hidden by default so it never
  // appears in the alarm interface, browser, or tests without the flag.
  const [liveModeEnabled, setLiveModeEnabled] = useState(false);
  // Advanced menu (manual data-file replay) shows on the -live UI when this host
  // doesn't use the replay_server_session broker.
  const [replayServerSession, setReplayServerSession] = useState(true);
  const [isLive, setIsLive] = useState(true);
  // Whether a replay is currently *playing* (running and not paused). Drives
  // File > Load Data's greyed state — Load Data stays available while paused, so
  // the user can load a new window after stopping to look around. Refreshed each
  // time a menu opens.
  const [replayPlaying, setReplayPlaying] = useState(false);
  // Active UI chrome theme, for the dot in File > Theme.
  const uiThemeId = useUiTheme();
  const barRef = useRef<HTMLDivElement>(null);
  const menuBar = buildMenuBar(
    diagFilterEnabled,
    toolWindowsOpen,
    reportTurbines,
    importCsdEnabled,
    turbineManualPresent,
    liveModeEnabled,
    liveModeEnabled && !replayServerSession,
    replayPlaying,
    isLive,
    replayServerSession,
    changePasswordEnabled,
    signOutEnabled,
    uiThemeId,
  );

  const closeMenu = useCallback(() => setOpenMenu(null), []);

  const handleToggleMode = useCallback(() => {
    setIsLive((prev) => {
      const next = !prev;
      // Optimistic flip; roll back if the backend rejects the switch so the UI
      // never desyncs from the actual session mode.
      setViewMode(next).catch((e) => {
        console.error("setViewMode failed:", e);
        setIsLive(prev);
      });
      return next;
    });
  }, []);

  // File > Load Data and Advanced > Select Data File are both inherently replay
  // actions (they load a historical window / binary data files). If the session
  // is currently live, switch to Replay *before* the picker opens — so the views
  // stop following live data and the replay toolbar appears — then forward the
  // action. Mirrors the C++ Load Data → Ti_replay_data_time and advanced file
  // replay paths, which flip the run state before loading. No-op in live-disabled
  // UIs or when already replaying.
  const dispatchMenuAction = useCallback((action: string) => {
    const switchesToReplay =
      action === "file.loadData" || action === "advanced.selectDataFile";
    if (switchesToReplay && liveModeEnabled && isLive) {
      setIsLive(false); // optimistic; roll back if the backend rejects
      setViewMode(false).catch((e) => {
        console.error("switch to replay failed:", e);
        setIsLive(true);
      });
    }
    onMenuAction(action);
  }, [liveModeEnabled, isLive, onMenuAction]);

  // File > Import CSD appears on sites tracking turbine serials — desktop and
  // web alike (the web uploads the picked file to /api/import_csd). The probe
  // reads as disabled on failure, so the item stays hidden.
  useEffect(() => {
    let cancelled = false;
    void getEnableTurbineSerial().then((enabled) => {
      if (!cancelled) setImportCsdEnabled(enabled);
    });
    return () => { cancelled = true; };
  }, []);

  // Web mirror of the diag-filter-enabled probe (the desktop probe lives in the
  // Tauri-gated effect below). Greys Diagnostics > Filters when no turbine has a
  // diag_latching.ini; leaves it enabled if the probe fails or 404s.
  useEffect(() => {
    if (typeof window === "undefined" || "__TAURI_INTERNALS__" in window) return;
    let cancelled = false;
    void fetch("/api/get_diag_filter_enabled")
      .then((r) => (r.ok ? (r.json() as Promise<boolean>) : true))
      .then((enabled) => { if (!cancelled) setDiagFilterEnabled(enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("__TAURI_INTERNALS__" in window)) return;
    let cancelled = false;
    (async () => {
      try {
        const { invoke } = await import("@tauri-apps/api/core");
        const enabled = await invoke<boolean>("get_diag_filter_enabled");
        if (!cancelled) setDiagFilterEnabled(enabled);
      } catch {
        // Leave the item enabled if the probe fails.
      }
    })();
    (async () => {
      try {
        let manual: string | null;
        if (isTauri()) {
          const { invoke } = await import("@tauri-apps/api/core");
          manual = await invoke<string | null>("get_turbine_manual");
        } else {
          // Browser/webserver: probe the /api route (scoped to the live tab's
          // turbine via X-Tiger-Tab) so the entry shows when a manual exists.
          const resp = await fetch("/api/get_turbine_manual");
          manual = resp.ok ? ((await resp.json()) as string | null) : null;
        }
        if (!cancelled) setTurbineManualPresent(!!manual);
      } catch {
        // Entry stays hidden if the probe fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const enabled = await getLiveModeEnabled();
      if (cancelled) return;
      if (!enabled) {
        setLiveModeEnabled(false); // switch stays hidden; nothing else to resolve
        return;
      }
      // Resolve the session's real run state BEFORE revealing the switch, so it
      // appears already showing the actual starting mode (replay by default)
      // rather than flashing the default LIVE for an IPC round-trip and then
      // correcting to REPLAY. setIsLive is committed before setLiveModeEnabled,
      // so the switch is never rendered with a stale mode.
      const s = await getViewRunState();
      if (cancelled) return;
      setIsLive(s === "live");
      setLiveModeEnabled(true);
      try {
        // Tauri → invoke; web → fetch (the helper handles both). On the -live web
        // tab this gates whether the Advanced manual-replay menu shows.
        const broker = await getReplayServerSession();
        if (!cancelled) setReplayServerSession(broker);
      } catch { /* leave default (broker assumed → Advanced hidden) */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep the Live/Replay toggle (and the ReplayToolbar it gates) in sync when
  // the session mode flips from somewhere other than this switch — e.g. a tool
  // window's "Replay From", another tab, or the backend loading a replay window on
  // OK. onViewModeChange follows the Tauri event on desktop and POLLS
  // get_view_run_state on web (the webserver has no event channel), so the toggle
  // recovers even if this window was backgrounded and missed the BroadcastChannel
  // ping. The mount sync is skipped — the effect above already resolves the
  // starting mode before revealing the switch.
  useEffect(() => onViewModeChange((live, isInitialSync) => {
    if (!isInitialSync) setIsLive(live);
  }), []);

  // Load the Tools > Reports cascade once: which built-in reports each turbine
  // enables plus any config-driven report/event entries.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    (async () => {
      try {
        let turbines: ReportMenuTurbine[];
        if ("__TAURI_INTERNALS__" in window) {
          const { invoke } = await import("@tauri-apps/api/core");
          turbines = await invoke<ReportMenuTurbine[]>("get_report_menu");
        } else {
          turbines = await fetch("/api/get_report_menu").then(
            (r) => r.json() as Promise<ReportMenuTurbine[]>,
          );
        }
        if (!cancelled) setReportTurbines(turbines);
      } catch {
        // No backend (e.g. tests): leave the Reports cascade hidden.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Track whether any tool window is open, to enable Window > Close All / Show
  // All. Desktop enumerates the Tauri windows on each menu open; the webserver
  // keeps a live BroadcastChannel registry, so subscribe once and update as tabs
  // open/close — no per-open ping race that could leave the entries wrongly
  // greyed while a backgrounded tab is slow to answer.
  useEffect(() => {
    let cancelled = false;
    countToolWindows().then((count) => {
      if (!cancelled) setToolWindowsOpen(count > 0);
    });
    const unsub = subscribeToolWindows(() => {
      if (!cancelled) setToolWindowsOpen(getOpenToolWindowCount() > 0);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Desktop has no live registry, so also recount whenever a menu opens.
  useEffect(() => {
    if (openMenu === null || !isTauri()) return;
    let cancelled = false;
    (async () => {
      const count = await countToolWindows();
      if (!cancelled) setToolWindowsOpen(count > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [openMenu]);

  // Refresh whether a replay is actively playing whenever a menu opens, so File >
  // Load Data and Advanced > Select Data File reflect the current run state when
  // the user reaches them. A paused replay counts as not playing, so these stay
  // enabled while paused.
  useEffect(() => {
    if (openMenu === null) return;
    let cancelled = false;
    (async () => {
      const s = await replay.getReplayStatus();
      if (!cancelled) setReplayPlaying(!!s?.active && !s?.paused);
    })();
    return () => {
      cancelled = true;
    };
  }, [openMenu]);

  useEffect(() => {
    if (openMenu === null) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu, closeMenu]);

  return (
    <div className="menu-bar" ref={barRef}>
      {menuBar.map((menu, i) => (
        <div
          key={menu.label}
          className={`menu-bar-item${openMenu === i ? " active" : ""}`}
          onMouseDown={() => setOpenMenu(openMenu === i ? null : i)}
          onMouseEnter={() => {
            if (openMenu !== null) setOpenMenu(i);
          }}
        >
          {menu.label}
          {openMenu === i && (
            <SubMenu
              items={menu.items}
              onAction={dispatchMenuAction}
              onClose={closeMenu}
            />
          )}
        </div>
      ))}
      {liveModeEnabled && !isLive && <ReplayToolbar onMenuAction={dispatchMenuAction} />}
      {liveModeEnabled && (
        <div
          className="mode-switch"
          role="switch"
          aria-checked={isLive}
          aria-label={getTigerResource("TIGER_UI_MODE_SWITCH_LABEL")}
          title={isLive ? getTigerResource("TIGER_UI_MODE_TIP_LIVE") : getTigerResource("TIGER_UI_MODE_TIP_REPLAY")}
          tabIndex={0}
          onMouseDown={(e) => { e.stopPropagation(); handleToggleMode(); }}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") { e.preventDefault(); handleToggleMode(); }
          }}
        >
          <span className={`mode-label${isLive ? " active-live" : ""}`}>{getTigerResource("TIGER_UI_MODE_LIVE")}</span>
          <span className={`mode-track${isLive ? " live" : " replay"}`}>
            <span className="mode-knob" />
          </span>
          <span className={`mode-label${!isLive ? " active-replay" : ""}`}>{getTigerResource("TIGER_UI_MODE_REPLAY")}</span>
        </div>
      )}
    </div>
  );
}
